// Eskom PARCC — RFI Register + RTM sign-off hooks (owner feedback point 4).
//
// Reads go through the Data Connect client SDK (admin-claim enforced server-side
// on the queries); writes likewise (the RFI/RTM mutations are admin-only @auth).
// The RFI register is bulk-imported replace-all (clear then insert each row).
import { useMutation, useQuery } from '@tanstack/react-query';
import { queryClient } from '../lib/queryClient';
import { useToast } from '../components/ui/Toast';
import { fetchRfiRegister, fetchRtmSignoff, fetchReportRuns, fetchReportNarratives, fetchRtmStatus } from '../lib/db';
import {
  clearRfiRegister,
  insertRfiRow,
  upsertRtmSignoff,
  upsertReportNarrative,
  deleteReportNarrative,
  upsertRtmStatus,
  recordAudit,
  type RfiInput,
  type RtmInput,
  type ReportNarrativeInput,
  type RtmStatusInput,
} from '../lib/dbWrite';

const RFI_KEY = ['rfiRegister'] as const;
const RTM_KEY = ['rtmSignoff'] as const;
const REPORT_RUNS_KEY = ['reportRuns'] as const;

/** The register of generated reports (admin-only). */
export function useReportRuns() {
  return useQuery({ queryKey: REPORT_RUNS_KEY, queryFn: fetchReportRuns });
}

/** Invalidate the report-run register (call after a new report is persisted). */
export function invalidateReportRuns(): void {
  void queryClient.invalidateQueries({ queryKey: REPORT_RUNS_KEY });
}

/** The RFI register (admin-only). */
export function useRfiRegister() {
  return useQuery({ queryKey: RFI_KEY, queryFn: fetchRfiRegister });
}

/** Per-division RTM sign-off status (admin-only). */
export function useRtmSignoff() {
  return useQuery({ queryKey: RTM_KEY, queryFn: fetchRtmSignoff });
}

/** Replace-all RFI import: clear the register, then insert each row sequentially. */
export function useImportRfi() {
  const { show } = useToast();
  return useMutation({
    mutationFn: async (rows: readonly RfiInput[]) => {
      await clearRfiRegister();
      for (const r of rows) await insertRfiRow(r);
      // ONE summary audit for the replace-all import (server-stamped actor).
      await recordAudit({
        action: 'import_rfi',
        entityType: 'RfiRegister',
        entityId: 'register',
        note: `${rows.length} row(s)`,
      });
      return rows.length;
    },
    onSuccess: (n: number) => {
      void queryClient.invalidateQueries({ queryKey: RFI_KEY });
      show(`Imported ${n.toLocaleString('en-ZA')} RFI row(s).`, 'success');
    },
    onError: (e: unknown) =>
      show(e instanceof Error ? e.message : 'Failed to import the RFI register.', 'error'),
  });
}

/** Upsert one operating unit's RTM sign-off status. */
export function useUpsertRtm() {
  const { show } = useToast();
  return useMutation({
    mutationFn: async (input: RtmInput) => {
      await upsertRtmSignoff(input);
      await recordAudit({
        action: 'upsert_rtm',
        entityType: 'RtmSignoff',
        entityId: input.division,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: RTM_KEY });
      show('RTM status saved.', 'success');
    },
    onError: (e: unknown) =>
      show(e instanceof Error ? e.message : 'Failed to save the RTM status.', 'error'),
  });
}


// ---- Wednesday Composer: authored narratives + RTM status (Phase 1b) --------
const NARRATIVES_KEY = ['reportNarratives'] as const;
const RTM_STATUS_KEY = ['rtmStatus'] as const;

/** Persisted deck narratives for a financial year (admin-only). Idle until fy is set. */
export function useReportNarratives(fy: string | undefined) {
  return useQuery({
    queryKey: [...NARRATIVES_KEY, fy ?? ''],
    queryFn: () => fetchReportNarratives(fy as string),
    enabled: !!fy,
  });
}

/** Persisted reports-to-management status for a financial year (admin-only). */
export function useRtmStatus(fy: string | undefined) {
  return useQuery({
    queryKey: [...RTM_STATUS_KEY, fy ?? ''],
    queryFn: () => fetchRtmStatus(fy as string),
    enabled: !!fy,
  });
}

/** Save (or reset) an edited deck narrative, with an audit row. */
export function useUpsertReportNarrative() {
  const { show } = useToast();
  return useMutation({
    mutationFn: async (args: { input: ReportNarrativeInput; note?: string; payload?: unknown }) => {
      await upsertReportNarrative(args.input);
      await recordAudit({
        action: 'save_report_narrative',
        entityType: 'ReportNarrative',
        entityId: args.input.id,
        note: args.note,
        payload: args.payload,
      });
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: NARRATIVES_KEY }),
    onError: (e: unknown) => show(e instanceof Error ? e.message : 'Failed to save the narrative.', 'error'),
  });
}

/** Reset a narrative to its auto-draft (delete the override), with an audit row. */
export function useDeleteReportNarrative() {
  const { show } = useToast();
  return useMutation({
    mutationFn: async (id: string) => {
      await deleteReportNarrative(id);
      await recordAudit({ action: 'reset_report_narrative', entityType: 'ReportNarrative', entityId: id });
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: NARRATIVES_KEY }),
    onError: (e: unknown) => show(e instanceof Error ? e.message : 'Failed to reset the narrative.', 'error'),
  });
}

/** Save one reports-to-management status cell set, with an audit row. */
export function useUpsertRtmStatus() {
  const { show } = useToast();
  return useMutation({
    mutationFn: async (args: { input: RtmStatusInput; note?: string; payload?: unknown }) => {
      await upsertRtmStatus(args.input);
      await recordAudit({
        action: 'save_rtm_status',
        entityType: 'RtmStatus',
        entityId: args.input.id,
        note: args.note,
        payload: args.payload,
      });
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: RTM_STATUS_KEY }),
    onError: (e: unknown) => show(e instanceof Error ? e.message : 'Failed to save the RTM status.', 'error'),
  });
}
